import { useCallback } from 'react'

import { coreApi } from '../../../api/core'
import { useAppDispatch } from '../../../hooks/storeHooks'
import { latestEvolutionEntry } from '../components/helpers/blueprintEvolutionData'
import { buildLiveEventsBlueprintModalUrl, buildLiveEventsCalendarUrl } from '../components/helpers/calendarLinks'

// A hung request must not leave the tab the click opened blank.
export const CALENDAR_URL_RESOLVE_TIMEOUT_MS = 5000

interface ResolveBlueprintCalendarUrlParams {
  // Game-level blueprint whose newest event the dialog opens on.
  blueprintId: string
  gameId: string
  // The market blueprint's event type. Empty for a family placed under no event type
  // (the data glossary files those under "Uncategorized"), in which case the type is read
  // off the newest event itself.
  eventTypeId: string
}

/**
 * Returns a function that resolves a game-level blueprint's calendar link on demand: the
 * Event dialog on its newest event, or the plain grouped calendar when the
 * blueprint has no event to open, the lookup fails or it takes longer than
 * `CALENDAR_URL_RESOLVE_TIMEOUT_MS`. Reads through the RTK Query cache, so
 * a hover/focus prefetch makes it instant and a click before the data arrives simply waits
 * for it instead of settling for the calendar.
 */
export function useBlueprintCalendarUrlResolver() {
  const dispatch = useAppDispatch()

  return useCallback(
    ({ blueprintId, gameId, eventTypeId }: ResolveBlueprintCalendarUrlParams): Promise<string> => {
      const calendarUrl = buildLiveEventsCalendarUrl({ gameId })
      const resolveEventUrl = async () => {
        try {
          const evolution = await dispatch(
            coreApi.endpoints.getTrackedGameLiveEventBlueprints.initiate({ blueprintId, type: 'all' }, { subscribe: false })
          ).unwrap()
          const eventId = latestEvolutionEntry(evolution.evolution)?.eventId
          if (!eventId) return calendarUrl
          const resolvedTypeId =
            eventTypeId || (await dispatch(coreApi.endpoints.getTrackedEvent.initiate(eventId, { subscribe: false })).unwrap()).event?.typeId
          return resolvedTypeId ? buildLiveEventsBlueprintModalUrl({ gameId, eventTypeId: resolvedTypeId, eventId, evolutionScope: 'all' }) : calendarUrl
        } catch {
          return calendarUrl
        }
      }

      let timer: ReturnType<typeof setTimeout> | undefined
      const timeout = new Promise<string>((resolve) => {
        timer = setTimeout(() => resolve(calendarUrl), CALENDAR_URL_RESOLVE_TIMEOUT_MS)
      })
      return Promise.race([resolveEventUrl(), timeout]).finally(() => clearTimeout(timer))
    },
    [dispatch]
  )
}
